import { useInfiniteQuery, useQuery } from 'react-query'
import { Asset, queryAssets, queryBusinessCard, queryCompanyInfo, queryMenus, queryVisitors } from './backend'
import { useLocalBusinessCard } from '../store'
import { usePromise } from '../hooks'
import { Page } from '../types'

export const useCompanyInfo = () => {
  const { data } = useQuery({
    queryKey: 'companyInfo',
    queryFn: () => queryCompanyInfo()
  })
  return data
}
export const useBusinessCard = (cardId: string) => {
  const { data } = useQuery({
    queryKey: ['businessCard', cardId],
    queryFn: () => queryBusinessCard(cardId)
  })
  return data
}

export const useMenus = () => {
  const { data } = useQuery({
    queryKey: 'menus',
    queryFn: queryMenus
  })
  if (data) {
    return data.sort((a, b) => a.sort - b.sort)
  } else {
    return []
  }
}
export const useMenu = (menuId: number) => {
  const menus = useMenus()
  return menus.find(menu => menu.businessCardMenuId === menuId)
}

export const useVisitors = () => {
  const businessCard = useLocalBusinessCard()
  const { data } = useQuery({
    queryKey: ['visitors', businessCard?.businessCardId],
    queryFn: () => queryVisitors(businessCard!.businessCardId),
    enabled: businessCard !== undefined
  })
  return data
}

export const useAssetsPromise = (menuId: number, page: number): Promise<Page<Asset>> => {
  const { promise, resolve } = usePromise<Page<Asset>>([page])
  useQuery({
    queryKey: ['assets', { menuId, page }],
    queryFn: () => queryAssets(menuId, page),
    onSuccess: page => {
      resolve?.(page)
    }
  })
  return promise
}

export interface InfinitePage<T> {
  data: T[]
  page: number
  total: number
  hasNextPage: boolean
}
export interface InfiniteQueryResult<T> {
  data?: T[]
  fetchNext: () => Promise<void>
  hasNext: boolean
  isLoading: boolean
}
export const useAssetsInfinite = (menuId: number): InfiniteQueryResult<Asset> => {
  const { data, isLoading, isFetching, fetchNextPage, hasNextPage } = useInfiniteQuery<InfinitePage<Asset>>({
    queryKey: ['assets-infinite', menuId],
    queryFn: async ({ pageParam = 1}) => {
      const { content, totalElements, pageable: { pageNumber }, last } = await queryAssets(menuId, pageParam)
      return {
        data: content,
        page: pageNumber + 1,
        total: totalElements,
        hasNextPage: !last,
      }
    },
    getNextPageParam: ({ hasNextPage, page }) => hasNextPage ? page + 1 : undefined
  })
  return {
    data: data?.pages.flatMap(page => page.data),
    fetchNext: (fetchNextPage as any), // generic parameters of the returned Promise don't match
    hasNext: hasNextPage === undefined ? true : hasNextPage,
    isLoading: isLoading || isFetching
  }
}
